<template>
    <div class="config-stock-group">
        <div class="config-stock-card rounder-8 mb-4">
            <div class="filters-summary-header">
                <span class="filters-summary-icon">
                    <font-awesome-icon :icon="['fas', 'filter']" />
                </span>
                <span class="filters-summary-title">Filtros de productos:</span>
            </div>

            <div class="col-12 mb-3" v-if="authentication_user.active_store">
                <label class="form-label">{{ getFilterLabel('sku') }}</label>
                <div class="sku-input-wrapper">
                    <div class="sku-input-container">
                        <input 
                            type="text" 
                            class="form-control sku-input" 
                            v-model="skuInput"
                            placeholder="Buscar por SKU (Enter)"
                            @keydown.enter.prevent="addSkuTag"
                        />
                        <font-awesome-icon 
                            v-if="skuInput !== ''"
                            :icon="['fas', 'times']" 
                            class="clear-icon cursor-pointer"
                            @click="skuInput = ''"
                        />
                    </div>
                </div>
                <div v-if="selectedSkus.length > 0" class="tags-wrapper mt-2">
                    <div class="tags-container">
                        <span 
                            v-for="(sku, index) in selectedSkus.slice(0, 10)" 
                            :key="index"
                            class="tag"
                        >
                            {{ sku }}
                            <font-awesome-icon 
                                :icon="['fas', 'times']" 
                                class="tag-remove-icon"
                                @click="removeSkuTag(index)"
                            />
                        </span>
                        <span v-if="selectedSkus.length > 10" class="tag tag-more" :title="selectedSkus.slice(10).join(', ')">
                            +{{ selectedSkus.length - 10 }} más...
                        </span>
                    </div>
                    <button class="btn-clear-all" @click="clearSkus" title="Eliminar todos">
                        <font-awesome-icon :icon="['fas', 'trash']" />
                    </button>
                </div>
            </div>

            <div class="row g-3 mb-3">
                <div class="col-6" v-if="shouldShowFilter('supplier')">
                    <label class="form-label">{{ getFilterLabel('supplier') }}</label>
                    <v-select
                        :options="proveedores"
                        v-model="localFilters.supplier"
                        :reduce="proveedor => proveedor"
                        :placeholder="`Seleccionar ${getFilterLabel('supplier').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('supplier', $event)"
                    />
                </div>
                <div class="col-6" v-if="shouldShowFilter('brand')">
                    <label class="form-label">{{ getFilterLabel('brand') }}</label>
                    <v-select
                        :options="marcas"
                        v-model="localFilters.brand"
                        :reduce="marca => marca"
                        :placeholder="`Seleccionar ${getFilterLabel('brand').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('brand', $event)"
                    />
                </div>
                <div class="col-6" v-if="shouldShowFilter('select_category')">
                    <label class="form-label">{{ getFilterLabel('select_category') }}</label>
                    <filter-categories @getData="getCategory" class="custom-select" :category="localFilters.select_category"/>
                </div>
                <div class="col-6" v-if="shouldShowFilter('product_group')">
                    <label class="form-label">{{ getFilterLabel('product_group') }}</label>
                    <v-select
                        :options="groups"
                        v-model="localFilters.product_group"
                        :reduce="group => group"
                        :placeholder="`Seleccionar ${getFilterLabel('product_group').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('product_group', $event)"
                    />
                </div>
                <div class="col-6" v-if="shouldShowFilter('product_type')">
                    <label class="form-label">{{ getFilterLabel('product_type') }}</label>
                    <v-select
                        :options="filtros"
                        v-model="localFilters.product_type"
                        :reduce="filtro => filtro"
                        :placeholder="`Seleccionar ${getFilterLabel('product_type').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('product_type', $event)"
                    />
                </div>
                <div class="col-6" v-if="authentication_user.active_store">
                    <label class="form-label">{{ getFilterLabel('store') }}</label>
                    <v-select
                        :options="clusters"
                        v-model="localFilters.store"
                        label="name"
                        :reduce="store => store.name"
                        :placeholder="`Seleccionar ${getFilterLabel('store').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('store', $event)"
                    />
                </div>
            </div>

            <div class="divider-line"></div>

            <div class="active-filters-list" v-if="activeFilters.length > 0">
                <span 
                    v-for="(filter, index) in activeFilters" 
                    :key="index"
                    class="filter-chip"
                >
                    <span class="filter-chip-label">{{ filter.label }}</span>
                    <span class="filter-chip-value">{{ filter.value }}</span>
                </span>
            </div>
            <div v-else class="no-filters-msg">
                <span class="text-secondary size-13">Se deben aplicar filtros</span>
            </div>

            <div class="divider-line"></div>

            <!-- Tab navigation -->
            <div class="stock-tabs">
                <button 
                    class="stock-tab stock-tab--breakage"
                    :class="{ 'stock-tab--active': activeTab === 'breakage' }"
                    @click="activeTab = 'breakage'"
                >
                    <span class="stock-tab-dot stock-tab-dot--breakage"></span>
                    Quiebre inminente
                </button>
                <button 
                    class="stock-tab stock-tab--overstock"
                    :class="{ 'stock-tab--active': activeTab === 'overstock' }"
                    @click="activeTab = 'overstock'"
                >
                    <span class="stock-tab-dot stock-tab-dot--overstock"></span>
                    Sobrestock
                </button>
            </div>

            <!-- Tab content: Quiebre inminente -->
            <div class="stock-tab-content" v-if="activeTab === 'breakage'">
                <div class="stock-field">
                    <label class="stock-field-label">DÍAS DE COBERTURA MÍNIMOS</label>
                    <div class="input-with-suffix">
                        <input 
                            type="number" 
                            class="stock-input" 
                            v-model.number="coverageDaysMin"
                            min="0"
                            step="1"
                        />
                        <span class="input-suffix">días</span>
                    </div>
                </div>

                <div class="stock-field">
                    <label class="stock-field-label">UNIDADES MÍNIMAS EN ALMACÉN</label>
                    <div class="input-with-suffix">
                        <input 
                            type="number" 
                            class="stock-input" 
                            v-model.number="stockMin"
                            min="0"
                            step="1"
                        />
                        <span class="input-suffix">uds</span>
                    </div>
                </div>

                <div class="stock-field">
                    <label class="stock-field-label">ACCIÓN DE PRECIO</label>
                    <div class="input-with-suffix">
                        <select
                            class="stock-input stock-select"
                            v-model="priceActionBreakage"
                        >
                            <option value="" disabled>Seleccione una opción</option>
                            <option
                                v-for="option in priceActionOptions"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </option>
                        </select>
                        <span class="input-suffix">acción</span>
                    </div>
                </div>

                <div class="stock-field">
                    <label class="stock-field-label">% DE AJUSTE MÁXIMO</label>
                    <div class="input-with-suffix">
                        <input 
                            type="number" 
                            class="stock-input" 
                            v-model.number="priceAdjustmentBreakage"
                            step="0.1"
                        />
                        <span class="input-suffix">% alza</span>
                    </div>
                </div>
            </div>

            <!-- Tab content: Sobrestock -->
            <div class="stock-tab-content" v-if="activeTab === 'overstock'">
                <div class="stock-field">
                    <label class="stock-field-label">DÍAS DE COBERTURA MÁXIMOS</label>
                    <div class="input-with-suffix">
                        <input 
                            type="number" 
                            class="stock-input" 
                            v-model.number="coverageDaysMax"
                            min="0"
                            step="1"
                        />
                        <span class="input-suffix">días</span>
                    </div>
                </div>

                <div class="stock-field">
                    <label class="stock-field-label">% SOBRE INVENTARIO PROMEDIO</label>
                    <div class="input-with-suffix">
                        <input 
                            type="number" 
                            class="stock-input" 
                            v-model.number="inventoryAveragePercentage"
                            step="0.1"
                        />
                        <span class="input-suffix">%</span>
                    </div>
                </div>

                <div class="stock-field">
                    <label class="stock-field-label">ACCIÓN DE PRECIO</label>
                    <div class="input-with-suffix">
                        <select
                            class="stock-input stock-select"
                            v-model="priceActionOverstock"
                        >
                            <option value="" disabled>Seleccione una opción</option>
                            <option
                                v-for="option in priceActionOptionsStock"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </option>
                        </select>
                        <span class="input-suffix">acción</span>
                    </div>
                </div>

                <div class="stock-field">
                    <label class="stock-field-label">% DE DESCUENTO MÁXIMO</label>
                    <div class="input-with-suffix">
                        <input 
                            type="number" 
                            class="stock-input" 
                            v-model.number="priceAdjustmentOverstock"
                            step="0.1"
                        />
                        <span class="input-suffix">% baja</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="d-flex justify-content-end gap-2">
            <button class="btn btn-success fw-400 px-4" @click="saveStockGroup" :disabled="saving">
                <span v-if="saving">Guardando...</span>
                <span v-else>Guardar</span>
            </button>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import configsRecGroupFiltersMixin from '../mixins/configsRecGroupFiltersMixin';

export default {
    name: 'config-stock-group',
    mixins: [configsRecGroupFiltersMixin],
    props: {
        propHead: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            activeTab: 'breakage',

            // Quiebre
            coverageDaysMin: 0,
            stockMin: 0,
            priceActionBreakage: '',
            priceAdjustmentBreakage: 0,

            // Sobrestock
            coverageDaysMax: 0,
            inventoryAveragePercentage: 0,
            priceActionOverstock: '',
            priceAdjustmentOverstock: 0,

            saving: false,
            priceActionOptions: [
                { value: 'Subir el precio para desacelerar venta', label: 'Subir el precio para desacelerar venta' },
                { value: 'Bloquear cambio de precio', label: 'Bloquear cambio de precio' },
                { value: 'Solo alerta, sin accion', label: 'Solo alerta, sin accion' },
            ],
            priceActionOptionsStock: [
                { value: 'Bajar el precio para acelerar rotacion', label: 'Bajar el precio para acelerar rotacion' },
                { value: 'Activar promocion automatica', label: 'Activar promocion automatica' },
                { value: 'Solo alerta, sin accion', label: 'Solo alerta, sin accion' },
            ],
        };
    },
    methods: {
        ...mapActions(['update']),
        saveStockGroup() {
            const activeFilterParams = {};
            this.FILTER_KEYS.forEach(key => {
                if (key === 'sku') {
                    if (this.selectedSkus.length > 0) {
                        activeFilterParams[key] = this.selectedSkus.join(',');
                    }
                } else {
                    const val = this.localFilters[key];
                    if (val !== null && val !== undefined && val !== '' && val !== 0) {
                        activeFilterParams[key] = val;
                    }
                }
            });

            const data = this.activeTab === 'breakage'
                ? {
                    coverage_days_min: this.coverageDaysMin,
                    stock_min: this.stockMin,
                    price_action_breakage: this.priceActionBreakage,
                    price_adjustment_breakage: this.priceAdjustmentBreakage,
                    stock_situation: 'quiebre',
                    filters: activeFilterParams,
                }
                : {
                    coverage_days_max: this.coverageDaysMax,
                    inventory_average_percentage: this.inventoryAveragePercentage,
                    price_action_overstock: this.priceActionOverstock,
                    price_adjustment_overstock: this.priceAdjustmentOverstock,
                    stock_situation: 'sobrestock',
                    filters: activeFilterParams,
                };

            this.saving = true;

            this.update({
                url: `/api/products/config_stock/update_group/`,
                params: data,
            }).then(() => {
                this.saving = false;
                this.$emit('refrest');
                this.$notification.success('Configuración de stock actualizada para los productos filtrados.', { timer: 5, showCloseIcn: true });
            }).catch((error) => {
                this.saving = false;
                if (error.status == 400) {
                    this.$notification.error(error.data.message || 'Error al guardar la configuración.', { timer: 5, showCloseIcn: true });
                } else {
                    this.$notification.error('Error al guardar la configuración.', { timer: 5, showCloseIcn: true });
                }
            });
        },
    }
};
</script>

<style scoped>
.config-stock-card {
    background: #ffffff;
}

.filters-summary-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.filters-summary-icon {
    color: #9ca3af;
    font-size: 0.85rem;
}

.filters-summary-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: #6b7280;
}

.active-filters-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
}

.filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f3f4f6;
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 0.8rem;
}

.filter-chip-label {
    color: #9ca3af;
    font-weight: 500;
}

.filter-chip-value {
    color: #374151;
    font-weight: 600;
}

.no-filters-msg {
    padding: 8px 0 16px;
}

.divider-line {
    height: 1px;
    background: #e5e7eb;
    margin-bottom: 20px;
}

/* Tab navigation */
.stock-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
}

.stock-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 16px;
    border: 2px solid #e5e7eb;
    border-radius: 999px;
    background: #ffffff;
    color: #6b7280;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;
}

.stock-tab:hover {
    border-color: #d1d5db;
}

.stock-tab--active.stock-tab--breakage {
    border-color: #dc2626;
    background: #fef2f2;
    color: #dc2626;
}

.stock-tab--active.stock-tab--overstock {
    border-color: #d97706;
    background: #fffbeb;
    color: #d97706;
}

.stock-tab-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.stock-tab-dot--breakage {
    background: #dc2626;
}

.stock-tab-dot--overstock {
    background: #d97706;
}

/* Tab content */
.stock-tab-content {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

/* Fields */
.stock-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.stock-field-label {
    font-size: 0.72rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.input-with-suffix {
    display: flex;
    align-items: center;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
    background: #ffffff;
    transition: border-color 0.2s ease;
}

.input-with-suffix:focus-within {
    border-color: #1AA179;
    box-shadow: 0 0 0 0.2rem rgba(26, 161, 121, 0.15);
}

.stock-input {
    flex: 1;
    border: none !important;
    outline: none !important;
    padding: 10px 12px;
    font-size: 0.9rem;
    color: #1f2937;
    background: transparent;
    box-shadow: none !important;
}

.stock-select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
}

.stock-input::placeholder {
    color: #9ca3af;
}

.input-suffix {
    padding: 10px 12px;
    background: #f3f4f6;
    color: #6b7280;
    font-size: 0.78rem;
    font-weight: 500;
    white-space: nowrap;
    border-left: 1px solid #e5e7eb;
}

.btn-success {
    background-color: #1AA179 !important;
    border-color: #1AA179 !important;
}

.btn-success:hover:not(:disabled) {
    background-color: #158a65 !important;
    border-color: #158a65 !important;
}

.btn-success:disabled {
    opacity: 0.65;
}

.custom-select {
    border-radius: 6px;
    height: 40px;
    font-size: 14px;
}

.form-label {
    font-weight: 400;
    font-size: 16px;
    margin-bottom: 6px;
}

.sku-input-wrapper {
    width: 100%;
}

.sku-input-container {
    display: flex;
    align-items: center;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 4px;
    height: 40px;
    padding: 0 12px;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.sku-input-container:focus-within {
    border-color: #1AA179;
    box-shadow: 0 0 0 0.2rem rgba(26, 161, 121, 0.25);
}

.sku-input {
    flex: 1;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    font-size: 14px;
    background: transparent;
    color: #495057;
}

.sku-input::placeholder {
    color: #6c757d;
    opacity: 1;
}

.clear-icon {
    color: #6c757d;
    font-size: 14px;
    cursor: pointer;
    margin-left: 8px;
}

.clear-icon:hover {
    color: #495057;
}

.tags-wrapper {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    width: 100%;
}

.tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    flex: 1;
}

.tag {
    background-color: #007bff;
    color: white;
    padding: 4px 8px;
    border-radius: 16px;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tag-more {
    background-color: #6c757d;
    cursor: default;
}

.tag-remove-icon {
    cursor: pointer;
    font-size: 12px;
    opacity: 0.8;
}

.tag-remove-icon:hover {
    opacity: 1;
}

.btn-clear-all {
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    padding: 6px 4px;
    font-size: 13px;
    opacity: 0.75;
}

.btn-clear-all:hover {
    opacity: 1;
}
</style>
